/**
 * Pilot personal gear — the reference-dependent add (per-category limits) and its removal.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import type { AddPilotGearCommand, RemovePilotGearCommand } from "../commands";
import { PILOT_GEAR_LIMITS, canAddPilotGear, pilotGearCategory, withPilotGear } from "../pilotGear";
import type { PilotData, RosterState } from "../types";
import { changed, requireCompendium, requirePilot, withPilot, type RosterApplyResult } from "./context";

// ── add_pilot_gear / remove_pilot_gear (reference-dependent add: per-category limit) ─

/**
 * Add a pilot personal-gear item. The gear list is a flat id list with no duplicates,
 * so an add of an already-held id is a no-op (state unchanged). On a genuine add the
 * reducer resolves the item (reference-dependent) to derive its category off `type`
 * and hard-rejects when that category is already at its RAW limit (2 weapon / 1 armor
 * / 3 gear, single-sourced through `pilotGear.ts`). No LL/license gate (Core p. 34).
 *
 * NOTE: the 5 armor items carry `bonuses` targeting pilot on-foot stats — those land
 * nowhere here; building a pilot stat compute is separate work. Don't add one here.
 *
 * Inverse: a symmetric `remove_pilot_gear` when the add actually added; if the id was
 * already held (no-op), the inverse is an `add_pilot_gear` no-op so the round-trip law
 * holds at the idempotent edge (the `set_core_bonus`/prev-state model).
 */
export function applyAddPilotGear(
  state: RosterState,
  cmd: AddPilotGearCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const prevPresent = pilot.gear.includes(cmd.gearId);

  // A genuine add resolves the item and runs the per-category limit gate; a re-add of
  // a held id is a no-op that never needs the compendium and can't exceed a limit.
  if (!prevPresent) {
    const compendiumR = requireCompendium(ctx);
    if (!compendiumR.ok) return compendiumR;
    const compendium = compendiumR.value;
    const item = compendium.getById("pilot_gear", cmd.gearId);
    if (!item) {
      throw new MissingContentError("pilot_gear", cmd.gearId, `pilot ${cmd.pilotId}`);
    }
    if (!canAddPilotGear({ pilot, item, compendium })) {
      const category = pilotGearCategory(item);
      return err(
        new CommandError(
          `add_pilot_gear: ${cmd.gearId} (${category}) would exceed the pilot ${category} limit ${PILOT_GEAR_LIMITS[category]}`,
        ),
      );
    }
  }

  const next: PilotData = { ...pilot, gear: withPilotGear(pilot.gear, cmd.gearId, true) };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: prevPresent ? "add_pilot_gear" : "remove_pilot_gear",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      gearId: cmd.gearId,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Remove a pilot personal-gear item. Raw-only — removal needs no compendium and never
 * overflows a limit. Its inverse re-adds the same id when one was actually removed
 * (restores the list), or — if the id wasn't present — removes again (a no-op
 * round-trip). Symmetric, no lossy capture (the `unequip_system` model).
 */
export function applyRemovePilotGear(
  state: RosterState,
  cmd: RemovePilotGearCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const prevPresent = pilot.gear.includes(cmd.gearId);

  const next: PilotData = { ...pilot, gear: withPilotGear(pilot.gear, cmd.gearId, false) };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: prevPresent ? "add_pilot_gear" : "remove_pilot_gear",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      gearId: cmd.gearId,
    },
    events: changed(cmd.pilotId),
  });
}
